// Tiptap（ProseMirror）はスキーマに無い HTML を読み込んだ時点で落とすので、見た目に効くものが失われないかを確かめる部品をここに集める

// <template> の中身は描画されないので、画像の読み込みや <style> の適用を起こさずに HTML を DOM にできる
const parseFragment = (html) => {
  const template = document.createElement('template');
  template.innerHTML = html;
  return template.content;
};

// 表示中の文書の要素に移すと、画像の読み込みや onerror などの属性のスクリプトが動く
const serializeFragment = (fragment) => {
  const template = document.createElement('template');
  template.content.append(fragment);
  return template.innerHTML;
};

// <font size> の 1〜7 は、ブラウザがこのキーワードと同じ大きさで描く
const FONT_SIZE_KEYWORDS = {
  1: 'x-small',
  2: 'small',
  3: 'medium',
  4: 'large',
  5: 'x-large',
  6: 'xx-large',
  7: 'xxx-large',
};

// CKEditor の書式を貼り付けるとブラウザが <span style> を <font> に変えて入るので、読み込む前に同じ見た目の <span style> に直して TextStyle で扱う
export const normalizeLegacyHtml = (html) => {
  const fragment = parseFragment(html);
  for (const font of fragment.querySelectorAll('font')) {
    const span = document.createElement('span');
    const declarations = [];
    for (const { name, value } of font.attributes) {
      if (name === 'face') {
        declarations.push(`font-family: ${value}`);
      } else if (name === 'color') {
        declarations.push(`color: ${value}`);
      } else if (name === 'size') {
        if (FONT_SIZE_KEYWORDS[value]) {
          declarations.push(`font-size: ${FONT_SIZE_KEYWORDS[value]}`);
        }
      } else if (name === 'style') {
        declarations.push(value);
      } else {
        span.setAttribute(name, value);
      }
    }
    if (declarations.length > 0) {
      span.setAttribute('style', declarations.join('; '));
    }
    span.append(...font.childNodes);
    font.replaceWith(span);
  }
  return serializeFragment(fragment);
};

// QR コードの画面の「このコードでプッシュ通知を作成」は記法だけを本文に入れるので、何にも囲まれていない記法は Tiptap が <p> で包む前に部品（stampsSyntax）にし、囲まないまま書き戻す
const STAMPS_SYNTAX = /\[\[GET_STAMP:[0-9]+,.+?\]\]/g;

export const markBareStampsSyntax = (html) => {
  const fragment = parseFragment(html);
  for (const node of [...fragment.childNodes]) {
    if (node.nodeType !== Node.TEXT_NODE) continue;
    const pieces = node.data.split(new RegExp(`(${STAMPS_SYNTAX.source})`));
    if (pieces.length === 1) continue;
    node.replaceWith(
      ...pieces.map((piece, index) => {
        if (index % 2 === 0) return piece;
        const marker = document.createElement('stamps-syntax');
        marker.dataset.syntax = piece;
        return marker;
      }),
    );
  }
  return serializeFragment(fragment);
};

// Tiptap はリストの項目と表のセルの中身を必ず <p> で包むが、CKEditor は <li>テキスト</li> の形で書いていたので、ただ包んだだけの <p> は外して今と同じ形で保存する
const unwrapSingleParagraph = (parent) => {
  const [child] = parent.children;
  if (
    parent.childNodes.length === 1 &&
    child?.tagName === 'P' &&
    child.attributes.length === 0
  ) {
    child.replaceWith(...child.childNodes);
  }
};

const toHex = (...channels) =>
  `#${channels.map((c) => Number(c).toString(16).padStart(2, '0')).join('')}`;

// Tiptap は複数の style をまとめるときに色を rgb() に書き換えるが、CKEditor は #rrggbb で書いていた
const restoreHexColors = (element) => {
  element.setAttribute(
    'style',
    element
      .getAttribute('style')
      .replace(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/g, (_, r, g, b) =>
        toHex(r, g, b),
      ),
  );
};

export const tidyTiptapHtml = (html) => {
  const fragment = parseFragment(html);
  for (const parent of fragment.querySelectorAll('li, td, th')) {
    unwrapSingleParagraph(parent);
  }
  for (const element of fragment.querySelectorAll('[style]')) {
    restoreHexColors(element);
  }
  for (const marker of fragment.querySelectorAll('stamps-syntax')) {
    marker.replaceWith(marker.dataset.syntax);
  }
  return serializeFragment(fragment);
};

// Tiptap は属性の並び、style の書き方、入れ子の span の分け方、空白を正規化するので、見た目が変わらない違いはそろえてから比べる

export const INLINE_TAGS = new Set([
  'A',
  'ABBR',
  'B',
  'BIG',
  'CITE',
  'CODE',
  'DEL',
  'EM',
  'FONT',
  'I',
  'INS',
  'KBD',
  'MARK',
  'Q',
  'S',
  'SMALL',
  'SPAN',
  'STRIKE',
  'STRONG',
  'SUB',
  'SUP',
  'TT',
  'U',
  'VAR',
]);

const ATOM_TAGS = new Set(['BR', 'EMBED', 'HR', 'IFRAME', 'IMG', 'INPUT']);

// Tiptap は <thead> なども <tbody> に書き出すが、属性が無ければ見た目は変わらない
const TRANSPARENT_TAGS = new Set(['TBODY', 'TFOOT', 'THEAD']);

// Tiptap は style の font-weight などからも太字を読み込んで <strong> で書き出すので、同じ見た目のタグと style をそろえる
const INLINE_TAG_STYLES = {
  B: ['font-weight', 'bold'],
  STRONG: ['font-weight', 'bold'],
  EM: ['font-style', 'italic'],
  I: ['font-style', 'italic'],
};
const INLINE_TAG_ALIASES = { STRIKE: 'S', DEL: 'S' };

const scratch = document.createElement('span');

// ブラウザに style を読ませると、ショートハンドの展開や色の書き方がそろう
const normalizeStyle = (style) => {
  scratch.setAttribute('style', style);
  const declarations = [];
  for (let i = 0; i < scratch.style.length; i++) {
    const property = scratch.style[i];
    let value = scratch.style.getPropertyValue(property).trim();
    if (property === 'font-weight' && /^(bold|[7-9]00)$/.test(value)) {
      value = 'bold';
    }
    declarations.push([property, value]);
  }
  scratch.removeAttribute('style');
  return declarations;
};

const normalizeAttributes = (element) =>
  [...element.attributes]
    .flatMap(({ name, value }) =>
      name === 'style'
        ? normalizeStyle(value).map(([property, v]) => `style.${property}=${v}`)
        : [`${name}=${value.trim()}`],
    )
    .sort()
    .join(' ');

// Tiptap は入れ子の span を 1 つにまとめるので、文字ごとに祖先から集めた書式で比べ、style は内側の指定を勝たせる
const addInlineFormat = (format, element) => {
  const tagName = INLINE_TAG_ALIASES[element.tagName] ?? element.tagName;
  const styles = new Map(format.styles);
  const others = new Set(format.others);
  if (INLINE_TAG_STYLES[tagName]) {
    styles.set(...INLINE_TAG_STYLES[tagName]);
  } else if (tagName !== 'SPAN' && tagName !== 'FONT') {
    others.add(tagName);
  }
  for (const { name, value } of element.attributes) {
    if (name === 'style') {
      for (const [property, v] of normalizeStyle(value)) {
        styles.set(property, v);
      }
    } else {
      others.add(`${tagName}.${name}=${value.trim()}`);
    }
  }
  return { styles, others };
};

const formatKey = ({ styles, others }) =>
  [
    ...[...others].sort(),
    ...[...styles].map(([property, value]) => `${property}:${value}`).sort(),
  ].join(' ');

// \s だと詰めて描かれない &nbsp; まで詰めてしまう
const collapseWhitespace = (text) => text.replace(/[ \t\n\r\f]+/g, ' ');

const collectTokens = (node, format, tokens) => {
  for (const child of node.childNodes) {
    if (child.nodeType === Node.TEXT_NODE) {
      tokens.push({
        text: collapseWhitespace(child.data),
        format: formatKey(format),
      });
    }
    if (child.nodeType !== Node.ELEMENT_NODE) continue;

    if (ATOM_TAGS.has(child.tagName)) {
      tokens.push({
        mark: `<${child.tagName} ${normalizeAttributes(child)}> ${formatKey(format)}`,
      });
    } else if (INLINE_TAGS.has(child.tagName)) {
      collectTokens(child, addInlineFormat(format, child), tokens);
    } else if (
      TRANSPARENT_TAGS.has(child.tagName) &&
      child.attributes.length === 0
    ) {
      collectTokens(child, format, tokens);
    } else {
      tokens.push({ mark: `<${child.tagName} ${normalizeAttributes(child)}>` });
      collectTokens(child, format, tokens);
      tokens.push({ mark: `</${child.tagName}>` });
    }
  }
  return tokens;
};

// ブロックの境目の空白は描かれず、Tiptap も落とす
const tidyTokens = (tokens) => {
  const result = [];
  tokens.forEach((token, index) => {
    if (token.mark) {
      result.push(token.mark);
      return;
    }
    let { text } = token;
    if (!tokens[index - 1] || tokens[index - 1].mark?.startsWith('<')) {
      text = text.trimStart();
    }
    if (!tokens[index + 1] || tokens[index + 1].mark?.startsWith('<')) {
      text = text.trimEnd();
    }
    if (text === '') return;
    const last = result[result.length - 1];
    if (last?.format === token.format) {
      last.text += text;
    } else {
      result.push({ text, format: token.format });
    }
  });
  return result.map((item) =>
    typeof item === 'string' ? item : `${item.text} [${item.format}]`,
  );
};

const signature = (html) =>
  tidyTokens(
    collectTokens(
      parseFragment(html),
      { styles: new Map(), others: new Set() },
      [],
    ),
  );

const describeToken = (token) => {
  const tag = token.match(/^<\/?([A-Z0-9]+)/);
  if (tag) return `<${tag[1].toLowerCase()}> の付近`;
  return `「${token.replace(/ \[.*\]$/, '').slice(0, 20)}」の付近`;
};

// 失われるものが無ければ null を、あれば利用者が本文の中で探せるように最初に違う箇所を返す
export const findLostContent = (originalHtml, roundTrippedHtml) => {
  const before = signature(originalHtml);
  const after = signature(roundTrippedHtml);
  const index = before.findIndex((token, i) => token !== after[i]);
  if (index === -1) {
    return before.length === after.length
      ? null
      : describeToken(after[before.length]);
  }
  return describeToken(before[index]);
};
